import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAwaitControl } from 'react-redux-await-control';
import styled from 'styled-components';

import { Button, Flex, Input, Select, Typography } from '~/ui/components';
import useModals from '~/hooks/useModals';
import fetchOperationStepsByContract from '~/store/fetchOperationStepsByContract/action';
import { getContractDetails, updateOperationStep } from '~/store/contracts/actions';
import { EnumMappers } from '~/common';
import modalBuilder from '~/hoc/modalBuilder';

const { OperationStepType } = EnumMappers;

const MODAL_NAME = 'STEP_MODAL';

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.negative.primary};
  font-size: 12px;
  margin-top: 4px;
`;

type ModalStepProps = {
  contractId: string;
};

function ModalStep({ contractId }: ModalStepProps) {
  const [comment, setComment] = useState('');
  const [step, setStep] = useState<string | null>(null);

  const { closeModal } = useModals() as any;
  const dispatch = useDispatch();

  const steps = useSelector((state: any) => state.getIn(['fetchOperationStepsByContract']).getIn(['payload']));

  const [getContractControl, updateOperationStepControl] = useAwaitControl([getContractDetails, updateOperationStep]);
  const details = getContractControl.result();

  useEffect(() => {
    if (details?.operationStep) {
      setStep(details?.operationStep);
    }
  }, [details?.operationStep]);

  useEffect(() => {
    dispatch(fetchOperationStepsByContract(contractId));
  }, []);

  const handleSubmit = useCallback(() => {
    updateOperationStepControl.start({ contractId, operationStepType: step, comment });
    closeModal(MODAL_NAME)();
    getContractControl.start({ id: contractId });
  }, [step, comment, contractId]);

  const isValid = step !== details?.operationStep;
  const isAtLimit = comment.length >= 700;

  const stepOptions = (steps?.toArray?.() || []).map((item: string) => ({
    value: item,
    label: OperationStepType(item),
  }));

  return (
    <Flex direction="column" gap={18}>
      <Typography type="bodyMedium" weight="bold">
        Alterar passo da esteira
      </Typography>

      <Flex direction="column" gap={6} align="stretch">
        <Typography type="bodySmall">Passo da esteira:</Typography>
        <Select
          options={stepOptions}
          value={step ? [step] : undefined}
          onChange={(val: (string | number)[]) => setStep(val?.[0] as string)}
          placeholder="Selecione o novo passo"
        />
      </Flex>

      <Flex direction="column" gap={6}>
        <Typography type="bodySmall">Comentário:</Typography>
        <Input
          textArea
          rows={2}
          placeholder="Escreva um comentário (máx. 700 caracteres)"
          onChange={(value: any) => setComment(value)}
          value={comment}
          max={700}
        />
        {isAtLimit && <ErrorText>Limite de 700 caracteres atingido. Reduza o texto para continuar.</ErrorText>}
      </Flex>

      <Button color="primary" variant="contained" disabled={!isValid || isAtLimit} onClick={handleSubmit}>
        Alterar
      </Button>
    </Flex>
  );
}

export default modalBuilder(ModalStep, MODAL_NAME, {});
